import * as JSONbig from "json-bigint";
import { BigNumber } from "bignumber.js";
import { fromBigNumber } from "lib/Any64bitId";
import * as Api from "lib/Api";
import { observable } from "mobx";

interface ItemInterface {
  id: number;
  applicationId: number;
  eventId: number;
  env: string;
  nsaId: BigNumber | string;
  deviceId2: string;
  operationMode: number;
  productModel: number;
  json: string;
  lcRecordedAt: string;
  ncRecordedAt: string;
  reportIdentifier: string;
  postedAt: string;
  serialNumber: string;
  jsonErrors: { [index: string]: string[] };
  jsonWarnings: { [index: string]: string[] };
  eventMasterId: number;
  eventMasterDefinition: { [index: string]: string };
}

export class Item implements ItemInterface {
  @observable accessor id = 0;
  @observable accessor applicationId = 0;
  @observable accessor eventId = 0;
  @observable accessor env = "";
  @observable accessor nsaId: BigNumber | string = "";
  @observable accessor deviceId2: string = "";
  @observable accessor operationMode = 0;
  @observable accessor productModel = 0;
  @observable accessor json = "";
  @observable accessor lcRecordedAt = "";
  @observable accessor ncRecordedAt = "";
  @observable accessor reportIdentifier = "";
  @observable accessor postedAt = "";
  @observable accessor serialNumber = "";
  @observable accessor jsonErrors: { [index: string]: string[] } = {};
  @observable accessor jsonWarnings: { [index: string]: string[] } = {};
  @observable accessor eventMasterId = 0;
  @observable accessor eventMasterDefinition: { [index: string]: string } = {};

  constructor(props: Partial<ItemInterface>) {
    this.id = props.id ?? 0;
    this.applicationId = props.applicationId ?? 0;
    this.eventId = props.eventId ?? 0;
    this.env = props.env ?? "";
    this.nsaId = props.nsaId ?? "";
    this.deviceId2 = props.deviceId2 ?? "";
    this.operationMode = props.operationMode ?? 0;
    this.productModel = props.productModel ?? 0;
    this.json = props.json ?? "";
    this.lcRecordedAt = props.lcRecordedAt ?? "";
    this.ncRecordedAt = props.ncRecordedAt ?? "";
    this.reportIdentifier = props.reportIdentifier ?? "";
    this.postedAt = props.postedAt ?? "";
    this.serialNumber = props.serialNumber ?? "";
    this.jsonErrors = props.jsonErrors ?? {};
    this.jsonWarnings = props.jsonWarnings ?? {};
    this.eventMasterId = props.eventMasterId ?? 0;
    this.eventMasterDefinition = props.eventMasterDefinition ?? {};
  }

  static buildFromJsonApi(item: Api.RawPost, idScrambled: boolean) {
    const attributes = {
      ...item,
      nsaId:
        item.nsaId == null
          ? item.nsaId
          : idScrambled
            ? item.nsaId
            : new BigNumber(item.nsaId),
      deviceId2: item.deviceId2,
      jsonWarnings:
        item.jsonWarnings == null
          ? item.jsonWarnings
          : JSON.parse(item.jsonWarnings),
      jsonErrors:
        item.jsonErrors == null ? item.jsonErrors : JSON.parse(item.jsonErrors),
      eventMasterDefinition:
        item.eventMasterDefinition == null
          ? item.eventMasterDefinition
          : JSON.parse(item.eventMasterDefinition),
    };
    return new Item(attributes);
  }

  hasError() {
    return Object.keys(this.jsonErrors).length != 0;
  }

  hasUndefinedKeys() {
    return "undefined_keys" in this.jsonWarnings;
  }

  eventMasterNotDefined() {
    return "event_master_not_defined" in this.jsonWarnings;
  }

  systemEntries(displayIdAsHex: boolean) {
    const json = JSONbig.parse(this.json);
    const keyNames = Object.keys(json).filter((key) => key !== "data");
    const hexIdCommonKeys = new Set(
      displayIdAsHex ? ["application_id", "nsa_id"] : [],
    );

    const systemEntries = keyNames.map((k) => {
      let v = json[k];
      if (hexIdCommonKeys.has(k) && BigNumber.isBigNumber(v)) {
        v = fromBigNumber(v);
      }
      if (v instanceof Object) {
        // BigNumber
        v = v.toString();
      }
      if (typeof v === "boolean") {
        v = v.toString();
      }
      return [k, v as string] as [string, string];
    });
    return Object.fromEntries(systemEntries);
  }

  dataEntries(hexDataKeys: Set<string>) {
    const json = JSONbig.parse(this.json);
    let dataEntries: [string, string][] = [];
    for (const k of Object.keys(json.data)) {
      const v = json.data[k];
      dataEntries = dataEntries.concat(this.stringifyPair(k, v, hexDataKeys));
    }
    return Object.fromEntries(dataEntries);
  }

  private stringifyPair(
    k: string,
    v: unknown,
    hexDataKeys: Set<string>,
  ): [string, string][] {
    if (BigNumber.isBigNumber(v)) {
      if (hexDataKeys.has(k)) {
        return [[k, fromBigNumber(v)]];
      }
      return [[k, v.toString()]];
    }
    if (Array.isArray(v)) {
      return [[k, JSONbig.stringify(v, undefined, 2)]];
    }
    if (typeof v === "object") {
      return [[k, JSONbig.stringify(v, undefined, 2)]];
    }
    if (typeof v === "string") {
      return [[k, `"${v}"`]];
    }
    if (typeof v === "number") {
      if (hexDataKeys.has(k)) {
        return [[k, fromBigNumber(v)]];
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return [[k, String(v)]];
  }
}
